import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Upload } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { useAuth } from "./AuthContext";
import PageHeader, { formatRub } from "./PageHeader";
import { MAIN_DOC_TYPES, CIVIL_TILES, docTypeMeta, getDocUrl } from "./docTypes";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:5001";

const PERIODS = [
  { days: 7, label: "7Д", phrase: "за 7 дней" },
  { days: 30, label: "30Д", phrase: "за 30 дней" },
  { days: 90, label: "90Д", phrase: "за 90 дней" },
  { days: 365, label: "ГОД", phrase: "за год" },
];

const ACCEPTED_FILES = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

const plural = (n, one, few, many) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });

const formatWhen = (iso) => {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return `сегодня, ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `вчера, ${time}`;
  return `${d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })}, ${time}`;
};

const langPair = (r) => {
  const src = (r.source_language || "").toUpperCase();
  const tgt = (r.target_language || "ru").toUpperCase();
  return src && src !== "AUTO" ? `${src} → ${tgt}` : `→ ${tgt}`;
};

const SpendTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="px-chart-tip">
      {dayLabel(p.date)} · {formatRub(p.spend)}
    </div>
  );
};

const balanceNote = (user) => {
  const company = user?.company;
  if (company?.free_recognitions > 0)
    return `${company.free_recognitions} ${plural(
      company.free_recognitions,
      "бесплатное распознавание",
      "бесплатных распознавания",
      "бесплатных распознаваний",
    )}`;
  if (company?.price > 0) {
    const pages = Math.floor((user.balance || 0) / company.price);
    return `хватит на ${pages} ${plural(pages, "страницу", "страницы", "страниц")}`;
  }
  return null;
};

const Overview = ({ user, summary, days, onDays, loading }) => {
  const period = PERIODS.find((p) => p.days === days);
  const note = balanceNote(user);
  const peak = Math.max(1, ...(summary?.series || []).map((p) => p.spend));
  return (
    <div className="dash-overview">
      <div className="dash-overview-top">
        <div>
          <div className="dash-balance">{formatRub(user?.balance)}</div>
          <div className="dash-balance-sub">
            Баланс{note ? ` · ${note}` : ""}
          </div>
        </div>
        <div className="dash-period-spend">
          Расход {period.phrase}
          <strong>{loading ? "…" : formatRub(summary?.period_spend)}</strong>
        </div>
      </div>

      <div className="dash-chart" aria-hidden="true">
        {summary && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={summary.series}
              margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
            >
              {/* ноль поднят над кнопками периода, заливка уходит под них */}
              <YAxis hide domain={[-0.55 * peak, 1.15 * peak]} />
              <Tooltip
                content={<SpendTooltip />}
                cursor={{ stroke: "#7F9CC6", strokeWidth: 1.5, strokeDasharray: "4 4" }}
                offset={16}
              />
              <Area
                type="linear"
                dataKey="spend"
                stroke="#5B7FB8"
                strokeWidth={2}
                fill="#C9DBF2"
                fillOpacity={1}
                activeDot={{ r: 6, fill: "#5B8FD9", stroke: "#fff", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="dash-periods" role="group" aria-label="Период графика расхода">
        {PERIODS.map((p) => (
          <button
            key={p.days}
            type="button"
            className="dash-period"
            aria-pressed={p.days === days}
            aria-label={`Расход ${p.phrase}`}
            onClick={() => onDays(p.days)}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const monthMeta = (counts, key) => {
  const n = counts?.[key] || 0;
  return n > 0 ? `${n} за месяц` : null;
};

const RecentTable = ({ recent }) => {
  if (!recent.length)
    return (
      <div className="px-empty">
        Здесь появятся последние распознанные документы.{" "}
        <Link to="/recognition" className="px-link">
          Загрузить первый
        </Link>
      </div>
    );

  return (
    <div className="px-table-wrap">
      <table className="px-table" style={{ minWidth: 560 }}>
        <thead>
          <tr>
            <th scope="col">Документ</th>
            <th scope="col">Перевод</th>
            <th scope="col" className="num">
              Стоимость
            </th>
            <th scope="col">Загружен</th>
            <th scope="col">
              <span className="sr-only">Открыть</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {recent.map((r) => {
            const meta = docTypeMeta(r.document_type);
            const { Icon } = meta;
            const title = meta.fullLabel || meta.label;
            return (
              <tr key={r.id}>
                <td>
                  <span className="px-doc-cell">
                    <span className="px-doc-icon">
                      <Icon size={22} aria-hidden="true" />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span className="px-doc-name">{title}</span>
                      <span className="px-doc-sub">
                        {r.full_name || r.filename || `№ ${r.id}`}
                      </span>
                    </span>
                  </span>
                </td>
                <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  {langPair(r)}
                </td>
                <td className="num" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  {r.cost > 0 ? formatRub(r.cost) : "—"}
                </td>
                <td style={{ color: "var(--text-soft)", whiteSpace: "nowrap" }}>
                  {formatWhen(r.created_at)}
                </td>
                <td className="num" style={{ paddingRight: 0 }}>
                  <Link
                    to={getDocUrl(r.document_type, r.id)}
                    className="px-icon-btn is-small"
                    aria-label={`Открыть: ${title}${r.full_name ? `, ${r.full_name}` : ""}`}
                  >
                    <ArrowUpRight size={18} aria-hidden="true" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const UploadCard = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const openRecognition = (fileList) => {
    const files = Array.from(fileList || []);
    navigate("/recognition", files.length ? { state: { files } } : undefined);
  };

  return (
    <div className="px-cta">
      <div
        className={`px-cta-card${dragging ? " is-dragging" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          openRecognition(e.dataTransfer.files);
        }}
      >
        <svg className="px-cta-deco" viewBox="0 0 220 220" aria-hidden="true">
          <rect x="40" y="30" width="130" height="170" rx="16" fill="none" stroke="#5C5E66" strokeWidth="1.5" transform="rotate(-14 105 115)" />
          <rect x="52" y="34" width="130" height="170" rx="16" fill="none" stroke="#5C5E66" strokeWidth="1.5" transform="rotate(-4 117 119)" />
          <rect x="64" y="40" width="130" height="170" rx="16" fill="#16171B" stroke="#8B8D95" strokeWidth="1.5" transform="rotate(8 129 125)" />
        </svg>
        <h2 className="px-cta-title">
          Перетащите <span className="px-cta-pill">скан</span>
          <br />
          <span style={{ whiteSpace: "nowrap" }}>сюда, остальное — наше</span>
        </h2>
        <p className="px-cta-text">
          Паспорт, права, свидетельство или договор в PDF, DOCX, JPG или PNG —
          распознаем поля и соберём перевод в DOCX и PDF.
        </p>
        <div className="px-cta-actions">
          <button
            type="button"
            className="px-btn px-btn-sky"
            onClick={() => inputRef.current?.click()}
          >
            <Upload size={20} strokeWidth={2.2} aria-hidden="true" />
            Выбрать файл
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILES}
            hidden
            onChange={(e) => openRecognition(e.target.files)}
          />
        </div>
      </div>
    </div>
  );
};

const UserDashboard = () => {
  const { user } = useAuth();
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/dashboard/summary`, {
        params: { days, tz_offset: -new Date().getTimezoneOffset() },
      })
      .then((res) => {
        if (cancelled) return;
        setSummary(res.data);
        setError(null);
      })
      .catch(() => !cancelled && setError("Не удалось загрузить сводку"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString("ru-RU", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    [],
  );
  const todayCount = summary?.today_count || 0;
  const counts = summary?.month_counts;

  return (
    <>
      <PageHeader
        title="Рабочий стол"
        subtitle={today.charAt(0).toUpperCase() + today.slice(1)}
      />

      {error && (
        <p role="alert" className="px-empty" style={{ marginBottom: 24 }}>
          {error}
        </p>
      )}

      <section className="dash-row" aria-label="Обзор и новый документ">
        <div className="dash-col" style={{ flex: "1 1 400px" }}>
          <div className="dash-col-head">
            <h2 className="px-section-title">Обзор</h2>
          </div>
          <Overview
            user={user}
            summary={summary}
            days={days}
            onDays={setDays}
            loading={loading}
          />
        </div>

        <div className="dash-col" style={{ flex: "2 1 560px" }}>
          <div className="dash-col-head">
            <h2 className="px-section-title">Новый документ</h2>
            <Link to="/templates" className="px-link">
              Шаблоны и правила
            </Link>
          </div>
          <div className="px-tiles-big">
            {MAIN_DOC_TYPES.map(({ key, label, description, Icon, tone }) => (
              <Link
                key={key}
                to={`/recognition?type=${key}`}
                className={`px-tile tone-${tone}`}
              >
                <span className="px-tile-title">{label}</span>
                <span className="px-tile-sub">{description}</span>
                <span className="px-tile-foot">
                  <span className="px-tile-icon">
                    <Icon size={28} aria-hidden="true" />
                  </span>
                  <span className="px-tile-meta">
                    {monthMeta(counts, key) || (
                      <ArrowRight size={20} aria-hidden="true" />
                    )}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section style={{ marginTop: 32 }} aria-labelledby="civil-docs-title">
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <h2 id="civil-docs-title" className="px-section-title" style={{ fontSize: 20 }}>
            Документы РФ
          </h2>
          <p className="px-page-sub" style={{ margin: 0 }}>
            готовые шаблоны перевода на любой язык
          </p>
        </div>
        <div className="px-tiles-small" style={{ marginTop: 16 }}>
          {CIVIL_TILES.map(({ key, label, description, Icon }) => (
            <Link
              key={key}
              to={`/recognition?type=${key}`}
              className="px-tile-sm tone-butter"
              title={description}
            >
              <span className="px-tile-sm-icon">
                <Icon size={24} aria-hidden="true" />
              </span>
              <span className="px-tile-sm-label" style={{ minWidth: 0 }}>
                {label}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="dash-row" style={{ marginTop: 48, gap: 40, alignItems: "flex-start" }}>
        <div className="dash-col" style={{ flex: "999 1 560px" }}>
          <div className="dash-col-head" style={{ flexWrap: "wrap" }}>
            <h2 className="px-section-title">
              {loading && !summary
                ? "Последние документы"
                : todayCount > 0
                  ? `Сегодня ${plural(todayCount, "обработан", "обработано", "обработано")} ${todayCount} ${plural(todayCount, "документ", "документа", "документов")}`
                  : "Последние документы"}
            </h2>
            <Link to="/history" className="px-link">
              Вся история <ArrowRight size={16} strokeWidth={2.5} aria-hidden="true" />
            </Link>
          </div>
          {summary ? (
            <RecentTable recent={summary.recent || []} />
          ) : (
            loading && <div className="px-empty">Загружаем…</div>
          )}
        </div>

        <div style={{ flex: "1 1 480px", minWidth: 0 }}>
          <UploadCard />
        </div>
      </section>
    </>
  );
};

export default UserDashboard;
